import React, { useEffect, useMemo, useState } from 'react';
import SingleSelect from './ui/SingleSelect';
import StatsChart from './StatsChart';
import { SectorRow, StatsResponse } from '../types/client';
import { api } from '../api';
import { fmtEur, fmtNum } from '../utils/format';

const AppTab: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [sector, setSector] = useState<string>('');
  const [subsector, setSubsector] = useState<string>('');

  const [compareYear, setCompareYear] = useState<string>('');
  const [compareMonth, setCompareMonth] = useState<string>('');
  const [compareSector, setCompareSector] = useState<string>('');
  const [compareSubsector, setCompareSubsector] = useState<string>('');

  const [sectors, setSectors] = useState<SectorRow[]>([]);
  const [statsMain, setStatsMain] = useState<StatsResponse['data']>([]);
  const [statsCompare, setStatsCompare] = useState<StatsResponse['data']>([]);

  useEffect(() => {
    api.getSectors().then((res) => setSectors(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.getStatsApp(sector || undefined, subsector || undefined)
      .then((res) => setStatsMain(res.data))
      .catch(() => {});
  }, [sector, subsector]);

  useEffect(() => {
    if (!compareSector && !compareSubsector) {
      setStatsCompare([]);
      return;
    }
    api.getStatsApp(compareSector || undefined, compareSubsector || undefined)
      .then((res) => setStatsCompare(res.data))
      .catch(() => {});
  }, [compareSector, compareSubsector]);

  const years = useMemo(() => {
    const ys = new Set<number>();
    for (const r of statsMain) {
      const y = Number(r.fecmes.slice(0, 4));
      if (!Number.isNaN(y)) ys.add(y);
    }
    const arr = Array.from(ys).sort((a, b) => b - a);
    if (arr.length === 0) return Array.from({ length: 20 }, (_, i) => currentYear - i);
    return arr;
  }, [currentYear, statsMain]);

  const yearOptions = useMemo(
    () => [{ value: '', label: 'Todos' }, ...years.map((y) => ({ value: String(y), label: String(y) }))],
    [years]
  );

  const totals = useMemo(() => {
    const rows = year
      ? statsMain.filter((r) => r.fecmes.startsWith(`${year}-`) && (month ? r.fecmes.slice(5, 7) === month : true))
      : statsMain.filter((r) => (month ? r.fecmes.slice(5, 7) === month : true));
    return rows.reduce(
      (acc, r) => ({
        pedidos: acc.pedidos + (r.total_pedidos ?? 0),
        lineas: acc.lineas + (r.total_lineas ?? 0),
        importe: acc.importe + (r.importe_total_eur ?? 0),
        numClientesMax: Math.max(acc.numClientesMax, r.total_clientes ?? 0),
      }),
      { pedidos: 0, lineas: 0, importe: 0, numClientesMax: 0 }
    );
  }, [month, statsMain, year]);

  const compareEnabled = Boolean(compareYear || compareMonth || compareSector || compareSubsector);

  const chartCompareLabel = useMemo(() => {
    if (compareSector && compareSubsector) return `${compareSector} / ${compareSubsector}`;
    if (compareSector) return compareSector;
    if (compareYear) return `Año ${compareYear}`;
    return 'Comparativo';
  }, [compareSector, compareSubsector, compareYear]);

  const chartMainLabel = useMemo(() => {
    if (sector && subsector) return `${sector} / ${subsector}`;
    if (sector) return sector;
    if (year) return `APP ${year}`;
    return 'APP';
  }, [sector, subsector, year]);

  const compareTotals = useMemo(() => {
    const source = (compareSector || compareSubsector) ? statsCompare : statsMain;
    const effYear = compareYear || year;
    const effMonth = compareMonth || month;
    const rows = effYear
      ? source.filter((r) => r.fecmes.startsWith(`${effYear}-`) && (effMonth ? r.fecmes.slice(5, 7) === effMonth : true))
      : source.filter((r) => (effMonth ? r.fecmes.slice(5, 7) === effMonth : true));
    return rows.reduce(
      (acc, r) => ({
        pedidos: acc.pedidos + (r.total_pedidos ?? 0),
        lineas: acc.lineas + (r.total_lineas ?? 0),
        importe: acc.importe + (r.importe_total_eur ?? 0),
        numClientesMax: Math.max(acc.numClientesMax, r.total_clientes ?? 0),
      }),
      { pedidos: 0, lineas: 0, importe: 0, numClientesMax: 0 }
    );
  }, [compareMonth, compareSector, compareSubsector, compareYear, month, statsCompare, statsMain, year]);

  // Pre-filtered data for the chart
  const chartMain = useMemo(
    () => (year ? statsMain.filter((r) => r.fecmes.startsWith(`${year}-`)) : statsMain),
    [statsMain, year]
  );

  const chartCompare = useMemo(() => {
    if (!compareEnabled) return null;
    const source = (compareSector || compareSubsector) ? statsCompare : statsMain;
    if (!year) return source;
    const effYear = compareYear || year;
    return source.filter((r) => r.fecmes.startsWith(`${effYear}-`));
  }, [compareEnabled, compareSector, compareSubsector, compareYear, statsCompare, statsMain, year]);

  const months = useMemo(
    () => [
      { value: '', label: 'Todos' },
      { value: '01', label: 'Enero' },
      { value: '02', label: 'Febrero' },
      { value: '03', label: 'Marzo' },
      { value: '04', label: 'Abril' },
      { value: '05', label: 'Mayo' },
      { value: '06', label: 'Junio' },
      { value: '07', label: 'Julio' },
      { value: '08', label: 'Agosto' },
      { value: '09', label: 'Septiembre' },
      { value: '10', label: 'Octubre' },
      { value: '11', label: 'Noviembre' },
      { value: '12', label: 'Diciembre' },
    ],
    []
  );

  const sectorOptions = useMemo(() => {
    const unique = Array.from(new Set(sectors.map((s) => s.sector))).sort();
    return [{ value: '', label: 'Todos' }, ...unique.map((s) => ({ value: s, label: s }))];
  }, [sectors]);

  const subsectorOptions = useMemo(() => {
    if (!sector) return [];
    const subs = Array.from(
      new Set(sectors.filter((s) => s.sector === sector && s.subsector).map((s) => s.subsector as string))
    ).sort();
    if (subs.length === 0) return [];
    return [{ value: '', label: 'Todos' }, ...subs.map((s) => ({ value: s, label: s }))];
  }, [sector, sectors]);

  const compareSubsectorOptions = useMemo(() => {
    if (!compareSector) return [];
    const subs = Array.from(
      new Set(sectors.filter((s) => s.sector === compareSector && s.subsector).map((s) => s.subsector as string))
    ).sort();
    if (subs.length === 0) return [];
    return [{ value: '', label: 'Todos' }, ...subs.map((s) => ({ value: s, label: s }))];
  }, [compareSector, sectors]);

  const compareYearOptions = useMemo(
    () => [{ value: '', label: 'Ninguno' }, ...years.map((y) => ({ value: String(y), label: String(y) }))],
    [years]
  );

  const compareMonthOptions = useMemo(
    () => [{ value: '', label: 'Ninguno' }, ...months.filter((m) => m.value !== '')],
    [months]
  );

  const compareSectorOptions = useMemo(
    () => [{ value: '', label: 'Ninguno' }, ...sectorOptions.filter((o) => o.value !== '')],
    [sectorOptions]
  );

  const handleSectorChange = (val: string) => {
    setSector(val);
    setSubsector('');
  };

  const handleCompareSectorChange = (val: string) => {
    setCompareSector(val);
    setCompareSubsector('');
  };

  return (
    <div className="tab-content active">
      <div className="section-title">Filtros</div>
      <div className="filters-row">
        <div className="filters-group">
          <div className="filters-item">
            <div className="field-label">Año</div>
            <SingleSelect
              value={year}
              onChange={setYear}
              options={yearOptions}
            />
          </div>
          <div className="filters-item">
            <div className="field-label">Mes</div>
            <SingleSelect value={month} onChange={setMonth} options={months} />
          </div>
          <div className="filters-item">
            <div className="field-label">Sector</div>
            <SingleSelect value={sector} onChange={handleSectorChange} options={sectorOptions} />
          </div>
          {subsectorOptions.length > 0 && (
            <div className="filters-item">
              <div className="field-label">Subsector</div>
              <SingleSelect value={subsector} onChange={setSubsector} options={subsectorOptions} />
            </div>
          )}
        </div>

        <div className="filters-group">
          <div className="filters-item">
            <div className="field-label">Año comparativo</div>
            <SingleSelect value={compareYear} onChange={setCompareYear} options={compareYearOptions} />
          </div>
          <div className="filters-item">
            <div className="field-label">Mes comparativo</div>
            <SingleSelect value={compareMonth} onChange={setCompareMonth} options={compareMonthOptions} />
          </div>
          <div className="filters-item">
            <div className="field-label">Sector comparativo</div>
            <SingleSelect value={compareSector} onChange={handleCompareSectorChange} options={compareSectorOptions} />
          </div>
          {compareSubsectorOptions.length > 0 && (
            <div className="filters-item">
              <div className="field-label">Subsector comparativo</div>
              <SingleSelect value={compareSubsector} onChange={setCompareSubsector} options={compareSubsectorOptions} />
            </div>
          )}
        </div>
      </div>

      <StatsChart
        main={chartMain}
        compare={chartCompare}
        mainLabel={chartMainLabel}
        compareLabel={chartCompareLabel}
        yearMode={year !== ''}
      />

      <div className="section-title">Resumen</div>
      <div className="metric-grid">
        <div className="metric">
          <div className="metric-label">Num. clientes</div>
          <div className="metric-value">{fmtNum(totals.numClientesMax)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Num. pedidos</div>
          <div className="metric-value">{fmtNum(totals.pedidos)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Num. líneas</div>
          <div className="metric-value">{fmtNum(totals.lineas)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Importe</div>
          <div className="metric-value">{fmtEur(totals.importe)}</div>
        </div>
      </div>

      {compareEnabled ? (
        <>
          <div className="section-title">Resumen comparativo</div>
          <div className="metric-grid">
            <div className="metric">
              <div className="metric-label">Num. clientes</div>
              <div className="metric-value">{fmtNum(compareTotals.numClientesMax)}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Num. pedidos</div>
              <div className="metric-value">{fmtNum(compareTotals.pedidos)}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Num. líneas</div>
              <div className="metric-value">{fmtNum(compareTotals.lineas)}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Importe</div>
              <div className="metric-value">{fmtEur(compareTotals.importe)}</div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AppTab;
